import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, X, Tag, Layers, FlaskConical, Package, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { useInView } from '../hooks/useInView';

const API = process.env.REACT_APP_BACKEND_URL;

const IMG_URL = (filename) => `${API}/api/static/products/${filename}`;

const FILTERS = [
  { id: 'all', label: 'All Products' },
  { id: 'jars', label: 'Jars' },
  { id: 'bottles', label: 'Bottles' },
  { id: 'lotion-pumps', label: 'Lotion Pumps' },
  { id: 'caps', label: 'Caps & Closures' },
  { id: 'cosmetics', label: 'Colour Cosmetics' },
];

// Fallback category image when product has no image yet
const CAT_FALLBACKS = {
  jars: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/0ka489yz_Jars%20website.png',
  bottles: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/x4ohc4rp_Bottles%20for%20website.png',
  'lotion-pumps': 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/m3o5jx2z_Pump%20website.png',
  caps: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/ofll9zml_Caps%20website.png',
  cosmetics: 'https://customer-assets.emergentagent.com/job_site-creator-1837/artifacts/tf7zckjr_Cc%20website.png',
};

export default function Products() {
  const [searchParams] = useSearchParams();
  const initialCat = searchParams.get('cat') || 'all';
  const [activeFilter, setActiveFilter] = useState(initialCat);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [ref, inView] = useInView();

  const fetchProducts = useCallback(async (cat) => {
    setLoading(true);
    try {
      const url = cat === 'all'
        ? `${API}/api/products`
        : `${API}/api/products?category=${cat}`;
      const res = await fetch(url);
      const data = await res.json();
      setProducts(data);
    } catch (e) {
      console.error('Failed to load products', e);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchProducts(activeFilter); }, [activeFilter, fetchProducts]);

  const handleFilter = (id) => {
    setActiveFilter(id);
    setSelectedProduct(null);
  };

  return (
    <main style={{ paddingTop: '80px', backgroundColor: '#FAF9F6', minHeight: '100vh' }}>
      {/* Banner */}
      <section className="py-20" style={{ backgroundColor: '#0B1B3D' }} data-testid="products-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase mb-3 font-medium" style={{ color: '#D4AF37' }}>Our Collection</p>
          <h1 className="font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif', fontSize: 'clamp(2.5rem, 5vw, 4rem)' }}>
            Product Catalogue
          </h1>
          <p className="text-base font-light mt-4 max-w-xl" style={{ color: 'rgba(255,255,255,0.6)' }}>
            From standard inventory to fully custom moulds — over 500 packaging options crafted to your brand's vision.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20" data-testid="products-listing">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {/* Filters */}
          <div
            ref={ref}
            className="flex flex-wrap gap-3 mb-12"
            style={{ opacity: inView ? 1 : 0, transform: inView ? 'none' : 'translateY(1rem)', transition: 'all 0.6s ease' }}
          >
            {FILTERS.map(f => (
              <button
                key={f.id}
                onClick={() => handleFilter(f.id)}
                className="px-5 py-2.5 text-xs tracking-[0.15em] uppercase transition-all duration-200"
                style={{
                  backgroundColor: activeFilter === f.id ? '#0B1B3D' : 'transparent',
                  color: activeFilter === f.id ? 'white' : '#4A4F5A',
                  border: `1px solid ${activeFilter === f.id ? '#0B1B3D' : 'rgba(11,27,61,0.2)'}`,
                }}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>

          {/* Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {[...Array(8)].map((_, i) => (
                <div key={`skeleton-${i}`} className="bg-white animate-pulse" style={{ boxShadow: '0 2px 20px rgba(11,27,61,0.04)' }}>
                  <div style={{ aspectRatio: '1/1', backgroundColor: '#F0EDE6' }} />
                  <div className="p-4 space-y-2">
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                    <div className="h-4 bg-gray-200 rounded w-3/4" />
                    <div className="h-3 bg-gray-200 rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : products.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-base font-light" style={{ color: '#4A4F5A' }}>
                No products in this category yet. More coming soon.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {products.map((product, idx) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  delay={idx * 50}
                  onClick={() => setSelectedProduct(product)}
                />
              ))}
            </div>
          )}

          <div className="mt-16 pt-12" style={{ borderTop: '1px solid rgba(11,27,61,0.08)' }}>
            <p className="text-base font-light mb-6" style={{ color: '#4A4F5A' }}>
              Don't see what you're looking for? We specialise in custom mould making and bespoke packaging.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-8 py-4 text-xs tracking-[0.2em] uppercase text-white transition-opacity hover:opacity-85"
              style={{ backgroundColor: '#0B1B3D' }}
              data-testid="products-custom-cta"
            >
              Enquire for Custom Packaging <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Slide-in Panel */}
      <ProductPanel product={selectedProduct} onClose={() => setSelectedProduct(null)} />
    </main>
  );
}

function ProductCard({ product, onClick }) {
  const [ref, inView] = useInView();
  const [imgError, setImgError] = useState(false);

  const imageSrc = !imgError && product.display_image
    ? IMG_URL(product.display_image)
    : CAT_FALLBACKS[product.category_slug] || CAT_FALLBACKS.jars;

  return (
    <div
      ref={ref}
      className="group bg-white cursor-pointer"
      onClick={onClick}
      style={{
        opacity: inView ? 1 : 0,
        transform: inView ? 'translateY(0)' : 'translateY(1.5rem)',
        transition: 'opacity 0.6s ease, transform 0.6s ease',
        boxShadow: '0 2px 20px rgba(11,27,61,0.04)',
      }}
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative overflow-hidden" style={{ aspectRatio: '1/1', backgroundColor: '#F7F5F0' }}>
        <img
          src={imageSrc}
          alt={product.name}
          className="w-full h-full object-contain p-3 transition-transform duration-700 group-hover:scale-105"
          onError={() => setImgError(true)}
        />
        <div className="absolute top-3 left-3">
          <span className="px-2 py-1 text-[10px] tracking-widest uppercase font-medium" style={{ backgroundColor: '#0B1B3D', color: 'white' }}>
            {product.polymer}
          </span>
        </div>
        {product.images && product.images.length > 1 && (
          <div className="absolute top-3 right-3">
            <span className="px-2 py-1 text-[10px] font-medium" style={{ backgroundColor: '#D4AF37', color: '#0B1B3D' }}>
              +{product.images.length} photos
            </span>
          </div>
        )}
        <div
          className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ backgroundColor: 'rgba(11,27,61,0.4)' }}
        >
          <span className="text-white text-xs tracking-[0.2em] uppercase font-medium">View Details</span>
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] tracking-widest uppercase font-medium px-2 py-0.5" style={{ backgroundColor: '#F0EDE6', color: '#D4AF37' }}>
            {product.polymer}
          </span>
        </div>
        <h3 className="font-medium mb-1" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: '1.1rem' }}>
          {product.name}
        </h3>
        <p className="text-xs font-light" style={{ color: '#4A4F5A' }}>
          {product.capacity}
        </p>
      </div>
    </div>
  );
}

function ProductPanel({ product, onClose }) {
  const [activeImg, setActiveImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);

  useEffect(() => {
    if (product) setActiveImg(0);
  }, [product]);

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') { if (lightbox) setLightbox(false); else onClose(); } };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightbox, onClose]);

  if (!product) {
    return (
      <>
        <div className="fixed inset-0 z-40 transition-opacity duration-300" style={{ pointerEvents: 'none', opacity: 0 }} />
        <div className="fixed top-0 right-0 h-full z-50" style={{ width: 'min(520px, 100vw)', transform: 'translateX(100%)', transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)', backgroundColor: '#FAF9F6' }} />
      </>
    );
  }

  const images = product.images || [];
  const hasImages = images.length > 0;
  const currentImgSrc = hasImages ? IMG_URL(images[activeImg]) : CAT_FALLBACKS[product.category_slug];

  const prevImg = () => setActiveImg(i => (i - 1 + images.length) % images.length);
  const nextImg = () => setActiveImg(i => (i + 1) % images.length);

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 transition-opacity duration-300"
        style={{ backgroundColor: 'rgba(11,27,61,0.45)', opacity: product ? 1 : 0, pointerEvents: product ? 'auto' : 'none' }}
        onClick={onClose}
      />

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center"
          style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
          onClick={() => setLightbox(false)}
        >
          <img
            src={currentImgSrc}
            alt={product.name}
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={e => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(false)}
            className="absolute top-6 right-6 text-white opacity-70 hover:opacity-100 transition-opacity"
          >
            <X size={28} />
          </button>
          {images.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); prevImg(); }} className="absolute left-6 text-white opacity-70 hover:opacity-100 transition-opacity p-2"><ChevronLeft size={32} /></button>
              <button onClick={(e) => { e.stopPropagation(); nextImg(); }} className="absolute right-16 text-white opacity-70 hover:opacity-100 transition-opacity p-2"><ChevronRight size={32} /></button>
            </>
          )}
        </div>
      )}

      {/* Slide-in Panel */}
      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col overflow-y-auto"
        style={{
          width: 'min(520px, 100vw)',
          backgroundColor: '#FAF9F6',
          transform: product ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '-8px 0 40px rgba(11,27,61,0.15)',
        }}
        data-testid="product-panel"
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 z-10 p-2 transition-opacity hover:opacity-60"
          style={{ color: '#0B1B3D' }}
          data-testid="product-panel-close"
          aria-label="Close"
        >
          <X size={20} />
        </button>

        {/* Main Image */}
        <div
          className="relative w-full flex-shrink-0 cursor-zoom-in"
          style={{ aspectRatio: '4/3', backgroundColor: '#F0EDE6' }}
          onClick={() => setLightbox(true)}
        >
          <img src={currentImgSrc} alt={product.name} className="w-full h-full object-contain p-4" />
          <div className="absolute bottom-3 right-3 p-1.5 rounded" style={{ backgroundColor: 'rgba(11,27,61,0.6)' }}>
            <ZoomIn size={14} color="white" />
          </div>
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImg(); }}
                className="absolute left-3 top-1/2 -translate-y-1/2 p-1.5 rounded transition-opacity hover:opacity-100 opacity-70"
                style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
              >
                <ChevronLeft size={18} style={{ color: '#0B1B3D' }} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImg(); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded transition-opacity hover:opacity-100 opacity-70"
                style={{ backgroundColor: 'rgba(255,255,255,0.9)' }}
              >
                <ChevronRight size={18} style={{ color: '#0B1B3D' }} />
              </button>
            </>
          )}
        </div>

        {/* Thumbnail Strip */}
        {images.length > 1 && (
          <div className="flex gap-2 px-4 py-3 flex-shrink-0" style={{ backgroundColor: '#F0EDE6' }}>
            {images.map((img, i) => (
              <button
                key={img}
                onClick={() => setActiveImg(i)}
                className="flex-shrink-0 transition-all duration-200"
                style={{
                  width: 56,
                  height: 56,
                  border: `2px solid ${i === activeImg ? '#D4AF37' : 'transparent'}`,
                  backgroundColor: '#fff',
                  overflow: 'hidden',
                }}
                data-testid={`thumb-${i}`}
              >
                <img src={IMG_URL(img)} alt={`View ${i + 1}`} className="w-full h-full object-contain p-1" />
              </button>
            ))}
          </div>
        )}

        {/* Content */}
        <div className="p-8 flex flex-col gap-6 flex-1">
          {/* Header */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] tracking-[0.2em] uppercase font-semibold px-2.5 py-1" style={{ backgroundColor: '#0B1B3D', color: 'white' }}>
                {product.code}
              </span>
              <span className="text-[10px] tracking-[0.15em] uppercase font-medium px-2.5 py-1" style={{ backgroundColor: '#F0EDE6', color: '#D4AF37' }}>
                {product.category}
              </span>
            </div>
            <h2 className="font-light mb-3" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#0B1B3D', fontSize: '1.75rem' }}>
              {product.name}
            </h2>
          </div>

          {/* Specs */}
          <div className="space-y-4" style={{ borderTop: '1px solid rgba(11,27,61,0.08)', paddingTop: '1.5rem' }}>
            <SpecRow icon={FlaskConical} label="Polymer / Material" value={product.polymer} />
            <SpecRow icon={Package} label="Available Capacities" value={product.capacity} />
            <SpecRow icon={Layers} label="Customisations & Finishes" value={product.customisations} />
          </div>

          {/* Notes */}
          {product.notes && (
            <div className="p-4" style={{ backgroundColor: '#F0EDE6', borderLeft: '3px solid #D4AF37' }}>
              <p className="text-xs tracking-[0.12em] uppercase mb-1 font-medium" style={{ color: '#D4AF37' }}>Additional Information</p>
              <p className="text-sm font-light" style={{ color: '#1A1D24' }}>{product.notes}</p>
            </div>
          )}

          {/* CTA */}
          <div className="mt-auto pt-4">
            <Link
              to={`/contact?subject=Product Enquiry — ${product.name} (${product.code})`}
              onClick={onClose}
              className="flex items-center justify-center gap-2 w-full py-4 text-xs tracking-[0.2em] uppercase text-white transition-opacity hover:opacity-85"
              style={{ backgroundColor: '#0B1B3D' }}
              data-testid="product-panel-enquire"
            >
              Request a Quote <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

function SpecRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <Icon size={15} className="mt-0.5 flex-shrink-0" style={{ color: '#D4AF37' }} />
      <div>
        <p className="text-[10px] tracking-[0.15em] uppercase mb-0.5 font-medium" style={{ color: '#4A4F5A' }}>{label}</p>
        <p className="text-sm font-light" style={{ color: '#1A1D24' }}>{value}</p>
      </div>
    </div>
  );
}
